<template>
    <div v-if="pageData">
        <div class="contact-page">
            <ContactHeroBanner />

            <div class="container mt-4">
                <div class="row">
                    <div class="col-12 col-md-6">
                        <div v-if="alert.status" :class="`alert alert-${alert.status}`" role="alert">
                            <div class="alert-title">
                                {{ alertData.title }}
                            </div>

                            <div v-html="alertData.message" class="alert-message" />
                        </div>

                        <form v-else class="form-container" id="contact_form" method="post">
                            <div class="input-wrapper">
                                <InputField
                                    :customErrorMessage="$t('form.fields.emailAddress.errorMessage')"
                                    :validationRules="{
                                        required: true,
                                        loqateEmail: true,
                                        max: 100,
                                    }"
                                    name="email"
                                    :label="$t('contactPage.email')"
                                    type="email"
                                    vuexKey="internal.contactForm"
                                />
                            </div>

                            <div class="input-wrapper">
                                <InputField
                                    :customErrorMessage="$t('form.fields.fullName.errorMessage')"
                                    :validationRules="{
                                        required: true,
                                        max: 100,
                                    }"
                                    name="fullName"
                                    :label="$t('contactPage.fullName')"
                                    type="text"
                                    vuexKey="internal.contactForm"
                                />
                            </div>

                            <div class="input-wrapper">
                                <InputField
                                    :customErrorMessage="$t('form.fields.message.errorMessage')"
                                    :validationRules="{
                                        required: true,
                                        max: 100,
                                    }"
                                    name="message"
                                    :label="$t('contactPage.message')"
                                    type="textarea"
                                    vuexKey="internal.contactForm"
                                />
                            </div>
                            <div class="input-wrapper text-right">
                                <button :disabled="busy" class="button secondary submit-button" @click="submit">
                                    {{ $t('contactPage.submit') }}

                                    <div
                                        class="spinner-border spinner-border-sm text-light ml-1"
                                        role="status"
                                        v-show="busy"
                                    >
                                        <span class="sr-only">{{ $t('form.general.loading') }}...</span>
                                    </div>
                                </button>
                            </div>
                        </form>
                    </div>

                    <div class="col-12 col-md-6 mt-3 mt-md-0">
                        <p class="introduction-text mb-3">
                            {{ $t('contactPage.introductionText') }}
                        </p>

                        <div class="email-block mb-3">
                            <p class="email-title">
                                <i class="fal fa-envelope mr-1 icon"></i>
                                {{ $t('contactPage.email') }}
                            </p>
                            <a class="subtext" :href="`mailto:${contactEmail}`">
                                {{ contactEmail }}
                            </a>
                        </div>

                        <div class="address-block">
                            <p class="address-title">
                                <i class="fas fa-map-marker-alt mr-1 icon"></i>
                                {{ $t('contactPage.address') }}
                            </p>
                            <p class="subtext" v-html="address"></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { url } from '@js/utilities/url.js'
import axios from 'axios'
import { useForm } from 'vee-validate'
import { useReCaptcha } from 'vue-recaptcha-v3'

export default {
    data() {
        return {
            alert: {
                status: null,
            },
            recaptchaToken: false,
            busy: false,
        }
    },
    setup() {
        const store = useStore()
        const { validate } = useForm()
        let recaptcha

        if (process.env.RECAPTCHA_SITE_KEY) {
            const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()
            recaptcha = async () => {
                await recaptchaLoaded()

                return executeRecaptcha('contact')
            }
        }

        return {
            store,
            validate,
            recaptcha,
        }
    },
    created() {
        this.emitter.on('pageData:loaded', () => {
            this.emitter.off('pageData:loaded')
        })
    },
    methods: {
        submit: async function (e) {
            e.preventDefault()
            this.busy = true

            const validation = await this.validate()

            if (!validation.valid) {
                this.busy = false
                return
            }

            if (process.env.RECAPTCHA_SITE_KEY) {
                this.recaptchaToken = await this.recaptcha()
            }

            let result

            try {
                result = await axios.post(url('/api/v2/contact'), {
                    customer_email: this.store.state.apply.internal.contactForm.email.value,
                    customer_full_name: this.store.state.apply.internal.contactForm.fullName.value,
                    customer_message: this.store.state.apply.internal.contactForm.message.value,
                    email_to: this.contactEmail,
                    token: this.recaptchaToken,
                })

                this.alert = result.data.alert
            } catch (error) {
                this.alert.status = 'error'
            }
        },
    },
    computed: {
        pageData: function () {
            if (this.store.state.global.page) {
                this.emitter.emit('pageData:loaded')
                return this.store.state.global.page
            }

            return null
        },
        alertData: function () {
            const alertData = this.alert

            if (alertData.status === 'success') {
                return {
                    title: this.pageData.success_title,
                    message: this.pageData.success_message,
                }
            } else if (alertData.status === 'error') {
                return {
                    title: this.$t('errors.pages.clientError.heading'),
                    message: this.$t('errors.pages.clientError.description'),
                }
            }

            return alertData
        },
        contactEmail: function () {
            if ('email_to' in this.pageData && this.pageData.email_to) {
                return this.pageData.email_to
            }

            return 'enquiries.monevo.com'
        },
        address: function () {
            if ('office_address' in this.pageData && this.pageData.office_address) {
                return this.pageData.office_address
            }

            return 'Glasshouse<br> Alderley Park<br> Nether Alderley<br> SK10 4ZE'
        },
    },
}
</script>

<style scoped lang="scss">
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.contact-page {
    .form-container {
        margin-top: 7px;

        .input-wrapper {
            margin-bottom: 38px;

            .error {
                position: absolute;
            }
        }

        .submit-button {
            padding: 0 40px;

            @media screen and (min-width: $bp-medium) {
                width: initial;
            }
        }
    }

    .alert {
        background-color: #f4f5f6;

        .alert-title {
            font-size: rem(20px);
            font-weight: 600;
        }
    }

    .email-title,
    .address-title {
        color: var(--text-dark);
        font-weight: 600;
        margin-bottom: 0;
    }

    .icon {
        min-width: 20px;
        color: var(--primary);
    }

    .subtext {
        margin-left: 27px;
    }
}
</style>

<style lang="scss">
.contact-page {
    .error {
        position: absolute;
    }
}
</style>
